import React, { useRef, useState, useEffect, useCallback, useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { createPortal } from 'react-dom';
import './Home.css';
import { getProfile, fetchAuthenticatedImage } from '../../../api/services/authService';
import { getPublicProperties } from '../../../api/services/propertyService';
import { getRentalStatus, submitMoveOutRequest } from '../../../api/services/rentalService';
import { getTenantScoreAnalytics } from '../../../api/services/analyticsService';
import PayRentEvidenceModal from './PayRentEvidenceModal';
import TenantKYCModal from './TenantKYCModal';
import ComingSoonModal from './ComingSoonModal';
import {
  MdVerified,
  MdOutlineReceiptLong,
  MdPayments,
  MdVerifiedUser,
  MdOutlineHome,
  MdHomeWork,
  MdBuild,
  MdExitToApp,
  MdClose,
  MdAttachMoney,
  MdEventAvailable,
} from 'react-icons/md';

const formatMoneyKes = (value) => {
  const num = Number(value);
  if (!Number.isFinite(num)) return '—';

  const hasCents = Math.floor(num) !== num;
  const formatted = num.toLocaleString(undefined, {
    minimumFractionDigits: hasCents ? 2 : 0,
    maximumFractionDigits: 2,
  });

  return `KES ${formatted}`;
};

const formatDueDate = (dueDate) => {
  if (!dueDate || Number.isNaN(dueDate.getTime())) return '—';
  return dueDate.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: '2-digit' });
};

const getNextDue = (rentDueDay) => {
  const dueDay = Number(rentDueDay);
  if (!Number.isFinite(dueDay) || dueDay < 1 || dueDay > 28) return null;

  const now = new Date();
  const candidate = new Date(now.getFullYear(), now.getMonth(), dueDay, 12, 0, 0, 0);
  if (candidate < now) {
    candidate.setMonth(candidate.getMonth() + 1);
  }

  const diffMs = candidate.getTime() - now.getTime();
  const days = Math.max(0, Math.ceil(diffMs / (1000 * 60 * 60 * 24)));

  return { dueDate: candidate, days };
};

const displayTenantScore = (value) => {
  const n = Number(value);
  if (!Number.isFinite(n)) return '—';
  return (Math.round(n * 10) / 10).toLocaleString(undefined, {
    minimumFractionDigits: 0,
    maximumFractionDigits: 1,
  });
};

const getTenantScoreBand = (score) => {
  const n = Number(score);
  if (!Number.isFinite(n)) {
    return { label: 'No score yet', className: 'score-badge--neutral' };
  }
  if (n >= 80) return { label: 'Excellent', className: 'score-badge--excellent' };
  if (n >= 65) return { label: 'Good', className: 'score-badge--good' };
  if (n >= 50) return { label: 'Fair', className: 'score-badge--fair' };
  if (n >= 35) return { label: 'Needs work', className: 'score-badge--low' };
  return { label: 'No Score Yet', className: 'score-badge--risk' };
};

const resolveKycStatusFromProfile = (profile) => {
  const topLevelStatus = String(profile?.kycStatus || '').toUpperCase();
  const nestedStatus = String(profile?.kyc?.status || '').toUpperCase();

  if (topLevelStatus) return topLevelStatus;
  if (nestedStatus) return nestedStatus;

  // If KYC docs/object exist from registration but status has not propagated yet, treat as pending.
  const hasKycObject = Boolean(profile?.kyc?.id);
  const hasKycDocs = Boolean(
    profile?.kyc?.idDocumentUrl ||
      profile?.kyc?.idDocumentBackUrl ||
      profile?.kyc?.selfieUrl
  );
  if (hasKycObject || hasKycDocs) return 'PENDING';

  return null;
};

const Home = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [kycStatus, setKycStatus] = useState(null);
  const [kycLoading, setKycLoading] = useState(true);
  const [tenantCode, setTenantCode] = useState(null);
  const [recommended, setRecommended] = useState([]);
  const [recommendedImages, setRecommendedImages] = useState({});
  const [rental, setRental] = useState(null);
  const [rentalLoading, setRentalLoading] = useState(true);
  const [rentalError, setRentalError] = useState(null);
  const [rentalRefetchKey, setRentalRefetchKey] = useState(0);
  const [payRentModalOpen, setPayRentModalOpen] = useState(false);
  const [moveOutModalOpen, setMoveOutModalOpen] = useState(false);
  const [moveOutDate, setMoveOutDate] = useState('');
  const [moveOutReason, setMoveOutReason] = useState('No reason');
  const [moveOutSubmitting, setMoveOutSubmitting] = useState(false);
  const [moveOutError, setMoveOutError] = useState('');
  const [moveOutSuccess, setMoveOutSuccess] = useState('');
  const [moveOutRequested, setMoveOutRequested] = useState(false);
  const [moveOutPortalRoot, setMoveOutPortalRoot] = useState(null);
  const [tenantScoreData, setTenantScoreData] = useState(null);
  const [tenantScoreLoading, setTenantScoreLoading] = useState(true);
  const [userId, setUserId] = useState(null);
  const [kycModalOpen, setKycModalOpen] = useState(false);
  const [comingSoonModalOpen, setComingSoonModalOpen] = useState(false);

  useEffect(() => {
    const fetchKycStatus = async () => {
      try {
        setKycLoading(true);
        const response = await getProfile();
        if (response.status === 'success' && response.data) {
          const kycStatusValue = resolveKycStatusFromProfile(response.data);
          setKycStatus(kycStatusValue);
          setTenantCode(response.data.tenantCode ?? response.data.tenant_code ?? null);
          setUserId(response.data.id || response.data.userId || null);
        }
      } catch (error) {
        console.error('Failed to fetch KYC status:', error);
        setKycStatus(null);
        setTenantCode(null);
      } finally {
        setKycLoading(false);
      }
    };

    fetchKycStatus();
  }, []);

  useEffect(() => {
    let cancelled = false;

    const fetchRental = async () => {
      try {
        setRentalLoading(true);
        setRentalError(null);
        const res = await getRentalStatus();
        if (cancelled) return;
        if (res?.status === 'success' && res?.data) {
          setRental(res.data);
        } else {
          setRental(null);
        }
      } catch (e) {
        if (!cancelled) {
          console.error('Failed to fetch rental status:', e);
          setRental(null);
          setRentalError(e.message || 'Could not load rent status.');
        }
      } finally {
        if (!cancelled) setRentalLoading(false);
      }
    };

    fetchRental();
    return () => {
      cancelled = true;
    };
  }, [rentalRefetchKey]);

  useEffect(() => {
    let cancelled = false;

    const fetchTenantScore = async () => {
      try {
        setTenantScoreLoading(true);
        const res = await getTenantScoreAnalytics(12);
        const payload = res?.data && typeof res.data === 'object' ? res.data : null;
        if (!cancelled) {
          setTenantScoreData(payload);
        }
      } catch (e) {
        if (!cancelled) {
          console.error('Failed to fetch tenant score:', e);
          setTenantScoreData(null);
        }
      } finally {
        if (!cancelled) {
          setTenantScoreLoading(false);
        }
      }
    };

    fetchTenantScore();
    return () => {
      cancelled = true;
    };
  }, []);

  const unit = rental?.unit || null;
  const monthlyRent = rental?.monthlyRent ?? unit?.monthlyRent;
  const rentDueDay = unit?.rentDueDay;
  const nextDue = getNextDue(rentDueDay);

  const unitDisplayName = rental?.unitName || unit?.name || unit?.unitNumber || null;
  const unitDisplayNumber = rental?.unitNumber || unit?.unitNumber || null;

  const rentAmount = formatMoneyKes(monthlyRent);
  const rentDueText = (() => {
    if (rentalLoading) return '';
    if (!rental) return '';
    if (nextDue) {
      return `Due in ${nextDue.days} days\non ${formatDueDate(nextDue.dueDate)}`;
    }
    if (rentDueDay != null && Number.isFinite(Number(rentDueDay))) {
      return `Rent due on day ${rentDueDay} each month`;
    }
    return 'Rent due date not set';
  })();

  const hasActiveRental = Boolean(rental && !rentalLoading);
  const propertyId = rental?.propertyId || null;
  const unitId = rental?.unitId || unit?.id || null;
  const noticeStatus = String(rental?.noticeStatus || '').toUpperCase();
  const alreadyOnNotice = Boolean(rental?.onNotice) || noticeStatus === 'PENDING' || noticeStatus === 'APPROVED';
  const isMoveOutActionDisabled = !hasActiveRental || moveOutRequested || alreadyOnNotice;
  const tenantScoreTotalMonths = Number(tenantScoreData?.totalMonthsCounted);
  const tenantHasScoreWindow = Number.isFinite(tenantScoreTotalMonths) && tenantScoreTotalMonths > 0;
  const showNeutralScore = !hasActiveRental || !tenantHasScoreWindow;
  const tenantScore = showNeutralScore ? 0 : tenantScoreData?.tenantScore;
  const tenantScoreBand = showNeutralScore ? getTenantScoreBand(null) : getTenantScoreBand(tenantScore);

  const openPayRentModal = useCallback(() => {
    if (rentalLoading) return;
    if (!rental) {
      navigate('/tenant/listings');
      return;
    }
    setPayRentModalOpen(true);
  }, [rentalLoading, rental, navigate]);

  const openMoveOutModal = useCallback(() => {
    if (isMoveOutActionDisabled) return;
    const defaultDate = new Date();
    defaultDate.setMonth(defaultDate.getMonth() + 2, 0);
    setMoveOutDate(defaultDate.toISOString().substring(0, 10));
    setMoveOutReason('No reason');
    setMoveOutError('');
    setMoveOutSuccess('');
    setMoveOutModalOpen(true);
  }, [isMoveOutActionDisabled]);

  const quickActionHandledRef = useRef(false);

  useEffect(() => {
    const quickAction = location?.state?.quickAction;
    if (!quickAction) return;
    if (quickActionHandledRef.current) return;

    const tryHandle = () => {
      if (quickAction === 'pay_rent') {
        if (rentalLoading) return false;
        openPayRentModal();
        return true;
      }
      if (quickAction === 'move_out') {
        if (rentalLoading) return false;
        openMoveOutModal();
        return true;
      }
      if (quickAction === 'verify') {
        setKycModalOpen(true);
        return true;
      }
      if (quickAction === 'rent_loan') {
        setComingSoonModalOpen(true);
        return true;
      }
      return true;
    };

    const handled = tryHandle();
    if (!handled) return;

    quickActionHandledRef.current = true;
    // Clear navigation state so refresh/back doesn't re-open the modal
    navigate('/tenant', { replace: true, state: null });
  }, [location?.state, navigate, openMoveOutModal, openPayRentModal, rentalLoading]);

  const closeMoveOutModal = useCallback(() => {
    if (moveOutSubmitting) return;
    setMoveOutModalOpen(false);
    setMoveOutError('');
  }, [moveOutSubmitting]);

  const handleSubmitMoveOut = useCallback(
    async (e) => {
      e.preventDefault();
      if (!propertyId || !unitId) {
        setMoveOutError('Active unit details are missing. Please refresh and try again.');
        return;
      }
      const date = String(moveOutDate || '').trim();
      if (!date) {
        setMoveOutError('Select a move-out date.');
        return;
      }

      try {
        setMoveOutSubmitting(true);
        setMoveOutError('');
        setMoveOutSuccess('');
        await submitMoveOutRequest(propertyId, unitId, {
          moveOutDate: date,
          reason: (moveOutReason || '').trim() || 'No reason',
        });
        setMoveOutRequested(true);
        setMoveOutSuccess('Move-out request submitted successfully.');
      } catch (err) {
        setMoveOutError(err?.message || 'Could not submit move-out request.');
      } finally {
        setMoveOutSubmitting(false);
      }
    },
    [propertyId, unitId, moveOutDate, moveOutReason]
  );

  useEffect(() => {
    setMoveOutRequested(alreadyOnNotice);
  }, [alreadyOnNotice]);

  useEffect(() => {
    if (!moveOutModalOpen) {
      setMoveOutPortalRoot(null);
      return undefined;
    }
    if (typeof document === 'undefined' || !document.body) {
      return undefined;
    }

    const el = document.createElement('div');
    el.setAttribute('data-move-out-modal-portal', '');
    document.body.appendChild(el);
    setMoveOutPortalRoot(el);

    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    return () => {
      document.body.style.overflow = prevOverflow;
      if (el.parentNode) {
        el.parentNode.removeChild(el);
      }
      setMoveOutPortalRoot(null);
    };
  }, [moveOutModalOpen]);

  useEffect(() => {
    const fetchRecommended = async () => {
      try {
        const response = await getPublicProperties({ page: 0, size: 5, search: '' });
        const payload = response.data || response;
        const content = Array.isArray(payload.content) ? payload.content : [];

        const mapped = content.slice(0, 5).map((property) => {
          const area = property.location?.area || '';
          const city = property.location?.city || '';
          const locationLabel = [area, city].filter(Boolean).join(', ') || property.address || '';
          const coverImageUrl =
            (Array.isArray(property.imageUrls) && property.imageUrls.length > 0
              ? property.imageUrls[0]
              : property.imageUrl) || null;

          return {
            id: property.id,
            title: property.name || 'Property',
            subtitle: locationLabel || 'Location not specified',
            imageUrl: coverImageUrl,
          };
        });

        setRecommended(mapped);

        const imagesMap = {};
        await Promise.all(
          mapped.map(async (item) => {
            try {
              if (!item.imageUrl) return;
              const blobUrl = await fetchAuthenticatedImage(item.imageUrl);
              if (blobUrl) {
                imagesMap[item.id] = blobUrl;
              }
            } catch (e) {
              console.error('Failed to load recommended image:', e);
            }
          })
        );
        setRecommendedImages(imagesMap);
      } catch (error) {
        console.error('Failed to fetch recommended properties:', error);
        setRecommended([]);
        setRecommendedImages({});
      }
    };

    fetchRecommended();
  }, []);

  const quickActions = useMemo(
    () => [
      { icon: MdEventAvailable, label: 'Booking requests', action: () => navigate('/tenant/bookings') },
      { icon: MdOutlineHome, label: 'Your Home', action: () => navigate('/tenant/your-home') },
      { icon: MdPayments, label: 'Payments', action: () => navigate('/tenant/payment-history') },
      { icon: MdAttachMoney, label: 'Rent Loan', action: () => setComingSoonModalOpen(true) },
      { icon: MdVerifiedUser, label: 'Verify', action: () => setKycModalOpen(true), kyc: true },
      { icon: MdOutlineReceiptLong, label: 'My bills', action: () => navigate('/tenant/my-bills') },
      { icon: MdBuild, label: 'Maintenance', action: () => navigate('/tenant/maintenance') },
      { icon: MdExitToApp, label: 'Request move out', action: openMoveOutModal, disabled: isMoveOutActionDisabled },
    ],
    [navigate, openMoveOutModal, isMoveOutActionDisabled]
  );

  const normalizedKycStatus = (kycStatus || '').toUpperCase();
  const isKycPending = normalizedKycStatus === 'PENDING';
  const isKycVerified = normalizedKycStatus === 'VERIFIED' || normalizedKycStatus === 'APPROVED';
  const canSubmitKyc = !kycLoading && !isKycPending && !isKycVerified;

  const getKycLabel = () => {
    if (isKycPending) return 'Pending';
    if (isKycVerified) return 'KYC Verified';
    return 'Submit KYC';
  };

  const getKycColors = () => {
    if (isKycVerified) {
      return { bg: '#C8E6C9', icon: '#2E7D32' };
    }
    if (isKycPending) {
      return { bg: '#ECEFF1', icon: '#607D8B' };
    }
    return { bg: 'rgba(211, 47, 47, 0.15)', icon: '#D32F2F' };
  };

  // handleVerifyClick will be implemented when verify functionality is added
  // const handleVerifyClick = () => {
  //   if (kycStatus === 'pending' || kycStatus === 'approved') return;
  //   navigate('/verify-identity');
  // };

  return (
    <div className="tenant-home">
      {/* Main Content */}
      <div className="tenant-home-content">
        {/* Rent Status Card — data from GET /api/rental/status */}
        <div
          className={`rent-status-card${rentalLoading ? ' rent-status-card--loading' : ''}${rentalError ? ' rent-status-card--error' : ''
            }${!rentalLoading && !rental && !rentalError ? ' rent-status-card--empty' : ''}`}
        >
          <div className="rent-status-content">
            <div className="rent-status-label">RENT STATUS</div>
            {tenantCode ? <div className="rent-status-tenant-id">Tenant Code: {tenantCode}</div> : null}
            {rentalLoading ? (
              <>
                <div className="rent-status-skeleton rent-status-skeleton--amount" />
                <div className="rent-status-skeleton rent-status-skeleton--line" />
              </>
            ) : rentalError ? (
              <>
                <div className="rent-status-amount rent-status-amount--muted">Couldn&apos;t load</div>
                <div className="rent-status-due">{rentalError}</div>
                <button type="button" className="rent-status-retry" onClick={() => setRentalRefetchKey((k) => k + 1)}>
                  Retry
                </button>
              </>
            ) : !rental ? (
              <>
                <div className="rent-status-amount rent-status-amount--small">No active lease</div>
                <div className="rent-status-due">When you rent a unit, your monthly rent and due date show here.</div>
              </>
            ) : (
              <>
                <div className="rent-status-amount">{rentAmount}</div>
                <div className="rent-status-due">
                  {rentDueText.split('\n').map((line, idx) => (
                    <React.Fragment key={idx}>
                      {line}
                      {idx === 0 ? <br /> : null}
                    </React.Fragment>
                  ))}
                </div>
              </>
            )}
          </div>
          {!rentalLoading && rentalError && (
            <button type="button" className="pay-now-btn pay-now-btn--secondary" onClick={() => navigate('/tenant/listings')}>
              Listings
            </button>
          )}
          {!rentalLoading && !rentalError && hasActiveRental && (
            <button type="button" className="pay-now-btn" onClick={openPayRentModal}>
              Pay Now
            </button>
          )}
          {!rentalLoading && !rentalError && !rental && (
            <button type="button" className="pay-now-btn" onClick={() => navigate('/tenant/listings')}>
              Explore listings
            </button>
          )}
        </div>

        {/* Score and Active Lease Snapshot Cards */}
        <div className="score-wallet-row">
          <div className="tenant-score-card">
            <div className="card-label">Tenant Score</div>
            <div className="score-display">
              {tenantScoreLoading ? (
                <span className="score-skeleton score-skeleton--value" aria-hidden />
              ) : (
                <span className="score-value">{displayTenantScore(tenantScore)}</span>
              )}
              <MdVerified className="verified-icon" />
            </div>
            {tenantScoreLoading ? (
              <div className="score-skeleton score-skeleton--badge" aria-hidden />
            ) : (
              <div className={`score-badge ${tenantScoreBand.className}`}>{tenantScoreBand.label}</div>
            )}
          </div>
          <div className="wallet-card">
            <div className="wallet-label-row">
              <div className="card-label">Active Lease</div>
              <MdHomeWork className="wallet-icon" />
            </div>
            {rentalLoading ? (
              <>
                <div className="score-skeleton score-skeleton--lease-title" aria-hidden />
                <div className="score-skeleton score-skeleton--lease-sub" aria-hidden />
              </>
            ) : rental ? (
              <>
                <div className="wallet-amount wallet-amount--compact" title={rental.propertyName || 'Property'}>
                  {rental.propertyName || 'Property'}
                </div>
                <div className="wallet-currency">
                  {(unitDisplayNumber || unitDisplayName || 'Unit')} - Active
                </div>
              </>
            ) : (
              <>
                <div className="wallet-amount wallet-amount--compact">No lease</div>
                <div className="wallet-currency">Explore listings</div>
              </>
            )}
          </div>
        </div>

        {/* Quick Actions */}
        <div className="quick-actions-section">
          <div className="tenant-home__section-title">Quick Actions</div>
          <div className="quick-actions-grid">
            {quickActions.map((action, index) => {
              const isVerify = action.kyc;
              const isDisabled = action.disabled || false;
              const colors = isVerify ? getKycColors() : { bg: '#E3F2FD', icon: '#0474C4' };
              const label = isVerify ? getKycLabel() : action.label;
              const clickable = (!isVerify || canSubmitKyc) && !isDisabled;

              return (
                <div
                  key={index}
                  className={`quick-action-item ${!clickable ? 'disabled' : ''}`}
                  onClick={clickable ? action.action : undefined}
                >
                  <div
                    className="quick-action-icon"
                    style={{ color: colors.icon, borderColor: `${colors.icon}55` }}
                  >
                    {React.createElement(action.icon, { style: { color: colors.icon, fontSize: '26px' } })}
                  </div>
                  <div className="quick-action-label">{label}</div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Recommended For You Section */}
        <div className="recommended-section">
          <div className="tenant-home__section-header">
            <div className="tenant-home__section-title">Recommended For You</div>
            <button className="view-all-btn" onClick={() => navigate('/tenant/listings')}>
              View All
            </button>
          </div>
          <div className="recommended-list">
            {recommended.map((item) => (
              <div
                key={item.id}
                className="recommended-card"
                onClick={() => navigate(`/tenant/property/${item.id}?scope=rental`)}
              >
                <div className="recommended-image-container">
                  {recommendedImages[item.id] ? (
                    <img
                      src={recommendedImages[item.id]}
                      alt={item.title}
                      className="recommended-image"
                    />
                  ) : (
                    <div className="recommended-placeholder">
                      <MdOutlineHome />
                    </div>
                  )}
                </div>
                <div className="recommended-card-content">
                  <div className="recommended-title">{item.title}</div>
                  <div className="recommended-price">{item.subtitle}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      <ComingSoonModal
        isOpen={comingSoonModalOpen}
        onClose={() => setComingSoonModalOpen(false)}
        title="Rent Loans"
        message="We are building a seamless way for you to access rent loans and short-term credit. Coming very soon!"
      />

      <PayRentEvidenceModal
        open={payRentModalOpen}
        onClose={() => setPayRentModalOpen(false)}
        rental={rental}
        onSubmitted={() => setRentalRefetchKey((k) => k + 1)}
      />

      {moveOutModalOpen && moveOutPortalRoot
        ? createPortal(
          <div className="tenant-modal-overlay" role="presentation">
            <button
              type="button"
              className="tenant-modal-backdrop"
              aria-label="Close move out request"
              onClick={closeMoveOutModal}
            />
            <div className="tenant-modal-panel" role="dialog" aria-modal="true" aria-labelledby="move-out-modal-title">
              <div className="tenant-modal-header">
                <h2 id="move-out-modal-title">Request Move Out</h2>
                <button type="button" className="tenant-modal-close" onClick={closeMoveOutModal} aria-label="Close">
                  <MdClose />
                </button>
              </div>

              {moveOutSuccess ? (
                <div className="tenant-modal-success">
                  <p>{moveOutSuccess}</p>
                  <button type="button" className="tenant-modal-submit" onClick={closeMoveOutModal}>
                    Close
                  </button>
                </div>
              ) : (
                <form className="tenant-modal-form" onSubmit={handleSubmitMoveOut}>
                  <div className="tenant-modal-row">
                    <label htmlFor="move-out-date">Move-out date</label>
                    <input
                      id="move-out-date"
                      type="date"
                      value={moveOutDate}
                      onChange={(e) => setMoveOutDate(e.target.value)}
                      disabled={moveOutSubmitting}
                      required
                    />
                  </div>

                  <div className="tenant-modal-row">
                    <label htmlFor="move-out-reason">Reason</label>
                    <textarea
                      id="move-out-reason"
                      rows={3}
                      value={moveOutReason}
                      onChange={(e) => setMoveOutReason(e.target.value)}
                      placeholder="No reason"
                      disabled={moveOutSubmitting}
                    />
                  </div>

                  {moveOutError ? <p className="tenant-modal-error">{moveOutError}</p> : null}

                  <div className="tenant-modal-actions">
                    <button
                      type="button"
                      className="tenant-modal-cancel"
                      onClick={closeMoveOutModal}
                      disabled={moveOutSubmitting}
                    >
                      Cancel
                    </button>
                    <button type="submit" className="tenant-modal-submit" disabled={moveOutSubmitting}>
                      {moveOutSubmitting ? 'Submitting...' : 'Submit request'}
                    </button>
                  </div>
                </form>
              )}
            </div>
          </div>,
          moveOutPortalRoot
        )
        : null}

      <TenantKYCModal
        open={kycModalOpen}
        onClose={() => setKycModalOpen(false)}
        userId={userId}
        onSubmitted={() => {
          getProfile().then(response => {
            if (response.status === 'success' && response.data) {
              setKycStatus(resolveKycStatusFromProfile(response.data));
            }
          }).catch(console.error);
        }}
      />
    </div>
  );
};

export default Home;
